// src/pages/TermsPage.js
//
// Public legal page — Participation Waiver + Terms of Use + Privacy.
//
// Linked from:
//   - Clerk's signup form (Terms of Service URL + Privacy Policy URL — set in
//     Clerk Dashboard → User & authentication → Legal). This is the primary
//     discovery surface and the legal moment of consent (Clerk's "Require
//     express consent" toggle renders an inline checkbox at signup pointing here).
//   - Account page → "Terms & Privacy" link.
//   - Direct URL `/terms`.
//
// Design choices:
//   - Standard scrollable page. NO modal, NO form inputs, NO scroll-to-bottom
//     enforcement, NO typed-signature ceremony. Replaces the legacy WaiverModal
//     post-signup gate (retired 2026-05-05) with industry-standard click-through
//     consent at the signup form itself.
//   - Content is fully admin-editable via siteConfig. Hardcoded English defaults
//     are baked in as fallback so the page always renders something legible even
//     if Firestore is unreachable.
//   - Public — no auth required. Renders identically signed-in or signed-out so
//     users can revisit any time, prospective users can read before signing up,
//     and Clerk's hosted signup form can deep-link to it.
//
// See `docs/ux/waiver-clerk-express-consent.md` and
// `docs/decisions/adr-waiver-clerk-express-consent.md`.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchSiteContent } from '../services/firebase';

// ── Hardcoded defaults ───────────────────────────────────────────────────────
// Used when siteConfig is unreachable OR when a specific field is empty. Keeps
// the page legally adequate on its own — same legal substance as the historical
// WaiverModal text, just delivered as a readable page rather than a scroll-jail
// modal. Update via the admin "Legal" tab; this is only the fallback.
const DEFAULTS = {
    termsPageTitle: 'Terms & Participation Waiver',
    termsPageIntro: 'Welcome — we\'re so glad you\'re here. This page is a simple read of what you agreed to when you signed up: how to participate safely, how we handle your account and your data, and how to reach us with questions. Take your time.',

    waiverParticipationTitle: 'Your Participation',
    waiverParticipationContent: 'By using Moments of Movement, you acknowledge that your participation is voluntary and involves physical movement and exercise. You are choosing to participate of your own free will.\n\nThe content here is designed to support gentle, low-impact movement and to help you build confidence over time. You are always encouraged to move at your own pace, choose modifications that feel right for your body, and rest whenever you need to.\n\nThe movement and content offered through the app are not personal training or individualized medical guidance. While general modifications and guidance may be offered, you are responsible for choosing the movements and intensity levels that are appropriate for you.',

    waiverRiskTitle: 'Risk and Responsibility',
    waiverRiskContent: 'You understand that physical activity carries inherent risks, including muscle soreness, strain, or injury. You acknowledge these risks and accept full responsibility for any injuries or damages that may occur as a result of your participation.\n\nYou agree to listen to your body and stop any movement that causes pain, dizziness, or discomfort.\n\nThe content provided through the app is for educational and informational purposes only and is not medical advice, diagnosis, or treatment. You confirm that you are physically able to participate, or that you have consulted a qualified healthcare professional before beginning if you have any medical conditions, injuries, are pregnant, or have other health concerns.',

    waiverReleaseTitle: 'Release',
    waiverReleaseContent: 'You agree to release, waive, and hold harmless Danielle Barr and Moments of Movement from any claims, injuries, damages, or health complications related to your use of the app and your participation in any content within it.',

    termsOfUseTitle: 'Terms of Use',
    termsOfUseContent: 'Welcome to Moments of Movement with Danielle. By creating an account and using this app, you agree to use it for personal, non-commercial purposes — moving, learning, and connecting with the community Danielle has built here.\n\nYour account is yours alone — please don\'t share login credentials or copy the videos and content for use outside the app.\n\nWe may update these Terms from time to time as the product grows. If we make a meaningful change, we\'ll let you know in-app or by email.',

    privacyTitle: 'Privacy',
    privacyContent: 'We collect what we need to make the app work and nothing more: your name, email, and account preferences (handled by our authentication provider, Clerk); your watch history and class engagement (so you can pick up where you left off); payment info (handled directly by Stripe — we never see your card number).\n\nWe don\'t sell your data. We don\'t show you ads.\n\nQuestions about your data, or want it deleted? Email us using the contact link in the app.',

    termsLastUpdated: '2026-05-05',
};

// Render a multi-paragraph string. Splits on \n\n for paragraph breaks (matches
// the textarea pattern Danielle uses in the admin editor — Enter for line breaks,
// Enter twice for new paragraphs). Single \n inside a paragraph is preserved as
// a soft break.
function FormattedBody({ text }) {
    if (!text) return null;
    const paragraphs = String(text).split(/\n\s*\n/).filter((p) => p.trim().length > 0);
    return paragraphs.map((para, i) => (
        <p key={i} style={{
            fontFamily: 'var(--mom-font-sans)',
            fontSize: 16,
            color: 'var(--mom-charcoal)',
            lineHeight: 1.75,
            marginBottom: i < paragraphs.length - 1 ? 18 : 0,
            whiteSpace: 'pre-line', // preserves single \n within a paragraph
        }}>
            {para}
        </p>
    ));
}

function Section({ title, body }) {
    return (
        <section style={{ marginBottom: 40 }}>
            <h2 style={{
                fontFamily: 'var(--mom-font-serif)',
                fontSize: 26,
                fontStyle: 'italic',
                color: 'var(--mom-charcoal)',
                marginBottom: 18,
                letterSpacing: '-0.01em',
            }}>
                {title}
            </h2>
            <FormattedBody text={body} />
        </section>
    );
}

function TermsPage({ previewContent } = {}) {
    // Merge: any non-empty siteConfig value overrides the default.
    const mergeTerms = (src) => {
        const merged = { ...DEFAULTS };
        Object.keys(DEFAULTS).forEach((key) => {
            if (src && src[key] && String(src[key]).trim().length > 0) merged[key] = src[key];
        });
        return merged;
    };
    // Start with defaults so the page renders instantly without a loading state.
    const [content, setContent] = useState(previewContent ? mergeTerms(previewContent) : DEFAULTS);

    useEffect(() => {
        // Admin live-preview: merge the draft content + skip the fetch. Without
        // previewContent (every real visitor) behavior is byte-identical.
        if (previewContent) { setContent(mergeTerms(previewContent)); return; }
        let cancelled = false;
        fetchSiteContent().then((c) => {
            if (cancelled || !c) return;
            setContent(mergeTerms(c));
        }).catch(() => { /* defaults already in state — no-op */ });
        return () => { cancelled = true; };
    }, [previewContent]);

    return (
        <div style={{
            background: 'var(--mom-cream)',
            minHeight: '100vh',
            position: 'relative',
            zIndex: 1,
        }}>
            {/* Subtle atmosphere consistent with other content pages (AboutMe, etc.) */}
            <div className="mom-atmosphere" />

            <div style={{
                maxWidth: 720,
                margin: '0 auto',
                padding: 'clamp(40px, 8vw, 80px) clamp(20px, 5vw, 32px)',
                position: 'relative',
                zIndex: 2,
            }}>
                {/* Back link — small, unobtrusive, top-left */}
                <Link to="/" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 13,
                    color: 'var(--mom-warm-gray)',
                    textDecoration: 'none',
                    marginBottom: 32,
                    transition: 'color 0.2s',
                }}>
                    <span style={{ fontSize: 16, lineHeight: 1 }}>←</span>
                    <span>Back to Moments of Movement</span>
                </Link>

                {/* Header */}
                <header style={{ marginBottom: 48 }}>
                    <h1 style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(32px, 5vw, 44px)',
                        fontStyle: 'italic',
                        color: 'var(--mom-charcoal)',
                        marginBottom: 20,
                        lineHeight: 1.15,
                        letterSpacing: '-0.015em',
                    }}>
                        {content.termsPageTitle}
                    </h1>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 17,
                        color: 'var(--mom-charcoal)',
                        lineHeight: 1.7,
                        opacity: 0.85,
                    }}>
                        {content.termsPageIntro}
                    </p>
                </header>

                <hr style={{
                    border: 'none',
                    borderTop: '1px solid var(--mom-cream-dark)',
                    margin: '0 0 48px',
                }} />

                {/* Participation Waiver — three sections, each numbered for legal continuity
                    with the historical waiver text users may have seen before. */}
                <Section title={`1. ${content.waiverParticipationTitle}`} body={content.waiverParticipationContent} />
                <Section title={`2. ${content.waiverRiskTitle}`} body={content.waiverRiskContent} />
                <Section title={`3. ${content.waiverReleaseTitle}`} body={content.waiverReleaseContent} />

                <hr style={{
                    border: 'none',
                    borderTop: '1px solid var(--mom-cream-dark)',
                    margin: '24px 0 48px',
                }} />

                {/* Terms of Use + Privacy — supporting documents, no numbering since
                    they're separate documents in spirit, not continuations of the waiver. */}
                <Section title={content.termsOfUseTitle} body={content.termsOfUseContent} />
                <Section title={content.privacyTitle} body={content.privacyContent} />

                {/* Footer meta — last-updated date. Quiet, low-emphasis. */}
                <footer style={{
                    marginTop: 24,
                    paddingTop: 32,
                    borderTop: '1px solid var(--mom-cream-dark)',
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 13,
                    color: 'var(--mom-warm-gray)',
                    textAlign: 'center',
                    lineHeight: 1.6,
                }}>
                    Last updated: {content.termsLastUpdated}
                </footer>
            </div>
        </div>
    );
}

export default TermsPage;
